import { useEffect, useCallback, useRef, useState } from 'react';
import { useApplicationStore } from '../store/application.store';
import { useNavigationStore } from '../store/navigation.store';
import { useLogoStore } from '../store/logo.store';
import { logger } from '@/features/help/utils/logger';
import { fetchApplicationData } from '../api/application.api';

interface UseApplicationFormSetupProps {
  userShortId?: string;
  mappingShortId?: string;
  signerShortId?: string;
  recordId?: string;
}

interface UseApplicationFormSetupReturn {
  isObjectTypeCheckLoading: boolean;
  isObjectTypeInvalid: boolean;
  imageLoading: boolean;
  loadingState: boolean;
  isReady: boolean;
}

// Purpose: Custom hook to handle all setup logic for the ApplicationForm component
export function useApplicationFormSetup({
  userShortId,
  mappingShortId,
  signerShortId,
  recordId,
}: UseApplicationFormSetupProps): UseApplicationFormSetupReturn {
  const shortId = userShortId;

  const {
    error,
    structure,
    initializeFromApiPayload,
    setError,
    setLoading: setStoreLoading,
    isBrokerTrialEnded,
    primaryCrmObject,
    crmId,
  } = useApplicationStore();

  const { fetchLogo, preloadLogoImage } = useLogoStore();
  const { getVisiblePages } = useNavigationStore();

  // Track if initialization is currently in progress to prevent double calls
  const isInitializing = useRef(false);
  // Track image loading status
  const [imageLoading, setImageLoading] = useState(true);
  // Track global loading status
  const [loadingState, setLoading] = useState(true);
  // Track object type validation
  const [isObjectTypeCheckLoading, setIsObjectTypeCheckLoading] = useState(true);
  const [isObjectTypeInvalid, setIsObjectTypeInvalid] = useState(false);
  const recaptchaScriptLoadedRef = useRef(false);
  const didAppendRecaptchaScriptRef = useRef(false);

  // New effect to validate object type after form data is loaded
  useEffect(() => {
    // Only run this check after the main data has been loaded into the store
    if (structure) {
      // If crmId is null, it's a new record. We only allow 'Lead' creation.
      if (crmId === null && primaryCrmObject !== 'Lead') {
        logger.error(
          'Application Form Setup',
          'Validation failed: Attempted to create a non-Lead record.',
          {
            primaryCrmObject,
            crmId,
          }
        );
        setIsObjectTypeInvalid(true);
      } else {
        // Validation passed - reset invalid state
        setIsObjectTypeInvalid(false);
      }

      // Object type validation is complete
      setIsObjectTypeCheckLoading(false);
    }
  }, [structure, primaryCrmObject, crmId]);

  // Log initial mount and params
  useEffect(() => {
    logger.info('Application Form Setup', 'Hook initialized', {
      shortId,
      mappingShortId,
      recordId,
      url: window.location.href,
      pathname: window.location.pathname,
    });

    // shortId is now managed by the application store - no localStorage needed
    logger.debug('Application Form Setup', 'shortId will be managed by store', { shortId });
  }, [shortId, mappingShortId, recordId]);

  // Load reCAPTCHA script when component mounts with retry logic
  useEffect(() => {
    let currentScript: HTMLScriptElement | null = null;

    const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

    const removeScript = () => {
      if (currentScript && document.head.contains(currentScript)) {
        document.head.removeChild(currentScript);
        currentScript = null;
      }
    };

    // Only load if not already loaded
    if (!window.grecaptcha) {
      logger.debug('Application Form Setup', 'Starting reCAPTCHA script loading with retry logic', {
        hasSiteKey: !!recaptchaSiteKey,
        siteKeyLength: recaptchaSiteKey?.length || 0,
        siteKeyFirstChars: recaptchaSiteKey ? recaptchaSiteKey.substring(0, 10) + '...' : 'none',
      });

      // Run the loader in an async IIFE so we can use try/catch
      void (async () => {
        try {
          const { withRetry, withTimeout, RETRY_CONFIG, TIMEOUT_DURATIONS } = await import(
            '@/features/application/utils/timeout.utils'
          );

          const loadRecaptchaScript = (): Promise<void> => {
            // Ensure previous script (if any) is removed before creating a new one
            removeScript();

            return new Promise((resolve, reject) => {
              const script = document.createElement('script');
              script.src = `https://www.google.com/recaptcha/enterprise.js?render=${recaptchaSiteKey}`;
              script.async = true;
              script.defer = true;

              currentScript = script;

              script.onload = () => {
                recaptchaScriptLoadedRef.current = true;
                logger.debug('Application Form Setup', 'reCAPTCHA script loaded successfully', {
                  timestamp: new Date().toISOString(),
                  hasGrecaptcha: !!window.grecaptcha,
                });
                resolve();
              };

              script.onerror = (error) => {
                removeScript();
                reject(new Error(`Failed to load reCAPTCHA script: ${String(error)}`));
              };

              didAppendRecaptchaScriptRef.current = true;
              document.head.appendChild(script);
            });
          };

          await withRetry(
            () => withTimeout(
              loadRecaptchaScript(),
              TIMEOUT_DURATIONS.RECAPTCHA_SCRIPT_LOAD,
              'reCAPTCHA script load timeout'
            ).catch((error) => {
              // Ensure cleanup on timeout/error between retries
              removeScript();
              throw error;
            }),
            RETRY_CONFIG.RECAPTCHA_LOAD,
            'reCAPTCHA Script Load'
          );
        } catch (error) {
          // If dynamic import failed or all retries exhausted, attempt a single safe load with manual timeout
          const fallbackTimeoutMs = 30000;
          logger.error('Application Form Setup', 'reCAPTCHA script loading encountered an error; using fallback loader', {
            error,
            fallbackTimeoutMs,
          });

          try {
            await new Promise<void>((resolve, reject) => {
              // Clean any existing script first
              removeScript();

              const script = document.createElement('script');
              script.src = `https://www.google.com/recaptcha/enterprise.js?render=${recaptchaSiteKey}`;
              script.async = true;
              script.defer = true;

              currentScript = script;

              const timeoutId = window.setTimeout(() => {
                removeScript();
                reject(new Error('reCAPTCHA script load timeout (fallback)'));
              }, fallbackTimeoutMs);

              script.onload = () => {
                recaptchaScriptLoadedRef.current = true;
                window.clearTimeout(timeoutId);
                logger.debug('Application Form Setup', 'reCAPTCHA script loaded successfully (fallback)', {
                  timestamp: new Date().toISOString(),
                  hasGrecaptcha: !!window.grecaptcha,
                });
                resolve();
              };

              script.onerror = (err) => {
                window.clearTimeout(timeoutId);
                removeScript();
                reject(new Error(`Failed to load reCAPTCHA script (fallback): ${String(err)}`));
              };

              didAppendRecaptchaScriptRef.current = true;
              document.head.appendChild(script);
            });
          } catch (finalError) {
            // Final failure after fallback attempt
            logger.error('Application Form Setup', 'reCAPTCHA script loading failed after fallback', {
              error: finalError,
              timestamp: new Date().toISOString(),
            });
            throw finalError;
          }
        }
      })();
    } else {
      // Already loaded
      recaptchaScriptLoadedRef.current = true;
    }

    // Cleanup: remove script if component unmounts before successful load
    return () => {
      const grecaptchaMissing = !window.grecaptcha;
      if (didAppendRecaptchaScriptRef.current && !recaptchaScriptLoadedRef.current && grecaptchaMissing) {
        removeScript();
      }
    };
  }, []);

  // Function to initialize the form
  const initialize = useCallback(
    async (retryCount = 0) => {
      if (!shortId || !mappingShortId) {
        logger.error('Application Form Setup', 'Missing required parameters', {
          hasShortId: !!shortId,
          hasMappingShortId: !!mappingShortId,
          shortId,
          mappingShortId,
          url: window.location.href,
          pathname: window.location.pathname,
        });
        setError('Unable to load application form: Missing required parameters');
        return;
      }

      try {
        // Set both global loading and image loading to true at the start
        setLoading(true);
        setStoreLoading(true);
        setImageLoading(true);

        logger.info('Application Form Setup', 'Starting initialization', {
          hasRecordId: !!recordId,
          hasShortId: !!shortId,
          hasMappingShortId: !!mappingShortId,
          shortId,
          mappingShortId,
          recordId,
          pathname: window.location.pathname,
          retryCount,
        });

        // Check for pdfOnly query parameter
        const urlParams = new URLSearchParams(window.location.search);
        const pdfOnly = urlParams.get('pdfOnly') === '1';

        // Fetch application data using the new API service
        const apiResponse = await fetchApplicationData({
          shortId,
          mappingShortId,
          recordId,
          signerShortId,
          pdfOnly,
        });

        // Handle redirect responses
        if (apiResponse.redirectToSigner && !apiResponse.redirectToRecord) {
          // Case: Marketing link with recordId but no signerShortId
          const newUrl = `${window.location.pathname}/${apiResponse.redirectToSigner}`;
          logger.info('Application Form Setup', 'Redirecting to canonical link with signer', {
            currentUrl: window.location.pathname,
            newUrl,
            signerShortId: apiResponse.redirectToSigner,
          });
          const finalUrl = `${newUrl}${window.location.search || ''}`;
          window.location.assign(finalUrl);
          return;
        }

        if (apiResponse.redirectToRecord && apiResponse.redirectToSigner) {
          // Case: Blank marketing link with no recordId
          const newUrl = `/application/${shortId}/${mappingShortId}/${apiResponse.redirectToRecord}/${apiResponse.redirectToSigner}`;
          logger.info(
            'Application Form Setup',
            'Redirecting to canonical link with record and signer',
            {
              currentUrl: window.location.pathname,
              newUrl,
              recordShortId: apiResponse.redirectToRecord,
              signerShortId: apiResponse.redirectToSigner,
            }
          );
          const finalUrl = `${newUrl}${window.location.search || ''}`;
          window.location.assign(finalUrl);
          return;
        }

        // Check if broker trial has ended before initializing
        const { setBrokerTrialEnded } = useApplicationStore.getState();
        setBrokerTrialEnded(!!apiResponse.brokerTrialEnded);

        // If trial ended, stop here and let ApplicationForm show TrialEnded component
        if (apiResponse.brokerTrialEnded) {
          setLoading(false);
          setStoreLoading(false);
          setImageLoading(false);
          logger.warn('Application Form Setup', 'Broker trial has ended, stopping initialization');
          return;
        }

        // No redirect needed - initialize stores with the payload
        // Include the URL-extracted IDs that are not returned by the API
        initializeFromApiPayload({
          ...apiResponse,
          recordId,
          signerShortId,
          userShortId: shortId,
        });

        // Find the first editable page after initialization
        const { editableFields } = useApplicationStore.getState();
        const { setActivePage, getVisiblePages } = useNavigationStore.getState();
        const visiblePages = getVisiblePages();
        let firstEditablePageIndex = visiblePages.findIndex((page) =>
          page.sections.some((section) =>
            section.fields.some((field) => editableFields.includes(field.id))
          )
        );
        if (firstEditablePageIndex === -1) {
          firstEditablePageIndex = 0;
        }
        setActivePage(firstEditablePageIndex);

        if (!apiResponse.structure) {
          logger.error('Application Form Setup', 'No structure returned from fetch');

          // Retry up to 3 times with exponential backoff
          if (retryCount < 3) {
            const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
            logger.info('Application Form Setup', `Retrying fetch in ${delay}ms`, {
              retryCount,
            });

            setTimeout(() => {
              initialize(retryCount + 1);
            }, delay);
            return;
          }

          // All loading states should be false on error
          setLoading(false);
          setStoreLoading(false);
          setImageLoading(false);
          setError('Unable to load application form: No structure found');
          return;
        }

        try {
          // Fetch logo only after structure is loaded
          logger.info('Application Form Setup', 'Starting logo fetch');
          await fetchLogo();
          logger.info('Application Form Setup', 'Logo fetch complete');

          // Then preload the logo image
          logger.info('Application Form Setup', 'Starting logo image preload');
          const preloadResult = await preloadLogoImage();

          logger.info('Application Form Setup', 'Logo preload completed', {
            success: preloadResult,
          });
          if (!preloadResult) {
            logger.warn(
              'Application Form Setup',
              'Logo preload was not successful, may load with delay'
            );
          }
        } catch (error) {
          logger.warn('Application Form Setup', 'Logo preloading failed, continuing anyway', {
            error,
          });
        } finally {
          // End all loading states at the same time
          setLoading(false);
          setStoreLoading(false);
          setImageLoading(false);
        }
      } catch (error: Error | unknown) {
        // Ensure all loading states are false on error
        setLoading(false);
        setStoreLoading(false);
        setImageLoading(false);

        logger.error('Application Form Setup', 'Error during initialization', {
          error,
          stack: error instanceof Error ? error.stack : undefined,
          shortId,
          mappingShortId,
          recordId: recordId || 'new',
          retryCount,
        });
        setError(
          error instanceof Error
            ? error.message
            : 'An error occurred while loading the application form'
        );
      }
    },
    [
      recordId,
      mappingShortId,
      shortId,
      initializeFromApiPayload,
      setError,
      fetchLogo,
      preloadLogoImage,
      setStoreLoading,
      signerShortId,
    ]
  );

  // Initialize the form when hook mounts
  useEffect(() => {
    // If we don't have the necessary IDs, or if initialization is already running, do nothing.
    if (!shortId || !mappingShortId || isInitializing.current) {
      return;
    }

    // Set the lock to prevent re-entry.
    isInitializing.current = true;
    initialize();
  }, [shortId, mappingShortId, initialize]);

  // Determine if the form is ready to be displayed
  const isReady = !!(
    !isObjectTypeInvalid &&
    !isObjectTypeCheckLoading &&
    !loadingState &&
    !imageLoading &&
    structure &&
    !error &&
    !isBrokerTrialEnded &&
    getVisiblePages().length > 0
  );

  return {
    isObjectTypeCheckLoading,
    isObjectTypeInvalid,
    imageLoading,
    loadingState,
    isReady,
  };
}
